<script setup>
import IconCalendarOutline from '../../icons/IconCalendarOutline.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlSkeleton from '../../xl/XlSkeleton.vue';
import XlTag from '../../xl/XlTag.vue';
import ScheduleRow from './ScheduleRow.vue';
import WeekLine from './WeekLine.vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // The rest of the week, rendered inside this same card so "1 more before
  // Sunday" reads as an extension of Today rather than a block of its own —
  // it used to be a sibling of this card in Overview.vue, which is what put it
  // outside the card the design puts it in.
  weekCount: { type: Number, default: 0 },
  weekHasDaysLeft: { type: Boolean, default: false },
  weekThroughLabel: { type: String, default: '' },
  weekTitles: { type: Array, default: () => [] },
  weekMore: { type: Boolean, default: false },
  weekRows: { type: Array, default: () => [] },
});

defineEmits(['open', 'act', 'retry', 'openSessions']);

const countLabel = (n) => (n === 1 ? '1 thing' : `${n} things`);
</script>

<template>
  <XlCard padded>
    <div class="today__head">
      <h2 class="today__title">Today</h2>
      <XlTag v-if="!loading && !error && rows.length" data-testid="today-count">
        {{ countLabel(rows.length) }}
      </XlTag>

      <!--
        The way out of a day and into the whole schedule, labelled and drawn as
        the prototype has it. It lands on the Sessions page already in its
        Calendar view, on this week (Overview: it opened the List).
      -->
      <XlButton
        variant="ghost"
        size="sm"
        class="today__all"
        data-testid="today-all-sessions"
        @click="$emit('openSessions')"
      >
        <template #icon><IconCalendarOutline color="currentColor" :size="14" /></template>
        Calendar
      </XlButton>
    </div>

    <!-- Three rows and the week's line, each the height of what is coming (a
         schedule row is ~68px, the week line 54px — measured), so the card is
         already the size it will be and what is under it stays put. -->
    <div v-if="loading" data-testid="today-loading">
      <XlSkeleton :lines="3" height="60px" />
      <div class="today__week-sk"><XlSkeleton width="45%" height="0.9em" radius="6px" /></div>
    </div>


    <div v-else-if="error" class="today__error" data-testid="overview-error">
      <p>{{ error }}</p>
      <XlButton variant="ghost" size="sm" data-testid="today-retry" @click="$emit('retry')">
        Try again
      </XlButton>
    </div>

    <!-- An honest empty state, not a blank card: a day with nothing on it is a
         fact worth stating. -->
    <p v-else-if="!rows.length" class="today__empty" data-testid="today-empty">
      Nothing scheduled today.
    </p>

    <div v-else>
      <ScheduleRow
        v-for="row in rows"
        :key="row.id"
        :row="row"
        @open="(r, el) => $emit('open', r, el)"
        @act="(r) => $emit('act', r)"
      />
    </div>

    <!-- Same card: the rest of the week is a continuation of Today, not a
         block of its own. -->
    <WeekLine
      v-if="!loading && !error"
      :class="{ 'today__week--ruled': rows.length > 0 }"
      :count="weekCount"
      :has-days-left="weekHasDaysLeft"
      :through-label="weekThroughLabel"
      :titles="weekTitles"
      :more="weekMore"
      :rows="weekRows"
      @open="(r, el) => $emit('open', r, el)"
      @act="(r) => $emit('act', r)"
    />
  </XlCard>
</template>

<style scoped>
/* The rest of the week is ruled off from today's rows, as the prototype's
   Today card draws it (todayCard: the last row's line, then the week line
   12px under it). Only under rows: an empty day has nothing to rule off. */
.today__week-sk {
  display: flex;
  align-items: center;
  height: 54px;
  margin-top: 8px;
  border-top: 1px solid var(--xl-color-border);
}

.today__week--ruled.today__week--ruled {
  margin-top: 0;
  padding-top: 11px;
  border-top: 1px solid var(--xl-color-border);
}

.today__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

/* 18px is off the scale, like the greeting's hero sizes: the scale jumps from
   16px straight to 22px, and neither reads as a card heading here. */
.today__title {
  margin: 0;
  font-size: 18px;
  font-weight: var(--xl-font-weight-bold);
  color: var(--xl-color-on-surface);
}

.today__all {
  margin-left: auto;
}

.today__empty {
  margin: 18px 0 6px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

.today__error {
  margin: 14px 0 4px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-on-surface);
}
</style>
